// KVKK: the personal data of an order's buyer (name, contact details, address, invoice party, the
// marketplace's copy of the order) is kept while the order needs it, then erased. What stays is what
// the warehouse and the seller still work with: numbers, lines, statuses, dates, city and district.
import type { MarketplaceChannel } from './marketplace';

export const PRIVACY_DEFAULTS = {
  /** days a closed order keeps its buyer's data (PII_RETENTION_DAYS on the worker and the CLI) */
  retentionDays: 30,
  /** a parcel shipped with no known delivery counts as delivered this many days after it left */
  deliveryGraceDays: 30,
  /** an open return, or a parcel the carrier brings back, holds the order this many days at most (never an Amazon order) */
  returnWaitDays: 90,
  /**
   * Amazon's data protection policy: an Amazon order's buyer data goes this many days after the
   * order closed at the latest, whatever its seller's own period (the default where that is shorter)
   * and whatever return is on its way: nothing holds it longer
   */
  amazonMaxDays: 30,
} as const;

/**
 * A seller's own period (tenants.personal_data_retention_days), set by a warehouse admin: for the
 * warehouse's own companies, which are the data controller of their buyers' data. Null is the default.
 */
export const SELLER_RETENTION_DAYS = { min: 30, max: 3650 } as const;

/** A seller with a period of its own, as the erasure's runs record it (code and days, nothing else). */
export interface SellerRetention {
  readonly code: string;
  readonly days: number;
}

/** Why a seller's own period is refused; null when it is acceptable (null days: back to the default). */
export function sellerRetentionProblem(days: number | null): string | null {
  if (days === null) return null;
  const { min, max } = SELLER_RETENTION_DAYS;
  return Number.isInteger(days) && days >= min && days <= max ? null : `Saklama süresi ${min} ile ${max} gün arasında tam gün olmalı`;
}

/**
 * Days an order keeps its buyer's data once it closed: its seller's own period, else the default;
 * an Amazon order never longer than Amazon allows. PrivacyService applies the same rule in SQL.
 */
export function keptDays(channel: MarketplaceChannel | string, sellerDays: number | null, defaultDays: number): number {
  if (channel === 'amazon') return Math.min(defaultDays, PRIVACY_DEFAULTS.amazonMaxDays);
  return sellerDays ?? defaultDays;
}

/** What screens show in place of the buyer once the data is gone. */
export const PERSONAL_DATA_ERASED = 'Kişisel veriler silindi';

const DAY = new Intl.DateTimeFormat('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'Europe/Istanbul' });

/** "Kişisel veriler silindi (23.10.2026)" */
export function personalDataErasedText(at: string | Date | null | undefined): string {
  if (!at) return PERSONAL_DATA_ERASED;
  return `${PERSONAL_DATA_ERASED} (${DAY.format(typeof at === 'string' ? new Date(at) : at)})`;
}

/** PII_RETENTION_DAYS as configured: unset is the default; anything but a whole number of days is refused. */
export function retentionDaysFrom(value: string | undefined | null): number {
  if (value === undefined || value === null || value.trim() === '') return PRIVACY_DEFAULTS.retentionDays;
  const days = Number(value.trim());
  if (!Number.isInteger(days) || days < 1 || days > 3650) {
    throw new Error(`PII_RETENTION_DAYS geçersiz: "${value}" (1 ile 3650 arasında tam gün sayısı olmalı)`);
  }
  return days;
}

/**
 * The fields of a marketplace's order line kept once the buyer's data is erased: ids, codes and
 * barcodes, the product's name, colour and size, quantities and prices. Kept at any depth (a price
 * object, Pazarama's product, Shopify's variant), nothing else: a Hepsiburada line also carries the
 * buyer's name, address and identity number, a PttAVM line the buyer's note.
 */
export const PAYLOAD_LINE_KEYS: readonly string[] = [
  // ids and codes
  'id', 'lineId', 'orderLineId', 'lineItemId', 'orderItemId', 'OrderItemId', 'order_line_id', 'productId', 'contentId',
  'productCode', 'hbSku', 'sku', 'merchantSku', 'merchantSKU', 'SellerSKU', 'ASIN', 'stockCode', 'code', 'erpId',
  'offer_sku', 'offer_id', 'product_sku', 'product_shop_sku', 'urunId',
  // barcodes
  'barcode', 'productBarcode', 'urunBarkod', 'variantBarkod',
  // the product as sold
  'productName', 'name', 'title', 'Title', 'product_title', 'urun', 'productColor', 'productSize', 'variantOptionDisplay',
  'variantAttributes', 'value', 'product', 'variant',
  // quantities
  'quantity', 'QuantityOrdered', 'toplamIslemAdedi', 'quantityUnit',
  // prices
  'price', 'unitPrice', 'lineUnitPrice', 'totalPrice', 'total_price', 'price_unit', 'origin_unit_price', 'listPrice', 'salePrice',
  'itemPrice', 'ItemPrice', 'invoicePrice', 'discount', 'lineTotalDiscount', 'totalDiscount', 'discountedTotalPrice',
  'discountAmount', 'PromotionDiscount', 'sellerDiscount', 'totalSellerDiscountPrice', 'sellerInvoiceAmount', 'taxAmount',
  'tax', 'taxes', 'rate', 'vatRate', 'kdvOrani', 'kdvHaricTutar', 'kdvHaricToplamTutar', 'kdvDahilToplamTutar', 'indirimToplam',
  'originalUnitPriceSet', 'shopMoney', 'amount', 'Amount', 'currency', 'currencyCode', 'CurrencyCode',
];

const KEPT = new Set(PAYLOAD_LINE_KEYS);

type Json = null | boolean | number | string | Json[] | { [key: string]: Json };

const isObject = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null && !Array.isArray(value);

/** The allowed keys of an object, at every depth; arrays keep their allowed elements. */
function keep(value: unknown): Json | undefined {
  if (value === null || typeof value === 'boolean' || typeof value === 'number' || typeof value === 'string') return value;
  if (Array.isArray(value)) return value.map(keep).filter((v): v is Json => v !== undefined);
  if (!isObject(value)) return undefined;
  const out: { [key: string]: Json } = {};
  for (const [key, inner] of Object.entries(value)) {
    if (!KEPT.has(key)) continue;
    const kept = keep(inner);
    if (kept !== undefined) out[key] = kept;
  }
  return out;
}

const array = (value: unknown): unknown[] => (Array.isArray(value) ? value : []);

/** Where each channel's payload lists its lines (the adapters' `raw`). */
function linesOf(channel: MarketplaceChannel | string, payload: Record<string, unknown>): unknown[] {
  switch (channel) {
    case 'ciceksepeti':
      // one order item is one payload: its product fields sit at the top
      return Array.isArray(payload.lines) ? payload.lines : [payload];
    case 'hepsiburada': {
      const lines = array(payload.lines);
      return lines.length > 0 ? lines : array(isObject(payload.package) ? payload.package.items : undefined);
    }
    case 'pazarama':
    case 'idefix':
    case 'amazon':
      return array(payload.lines ?? payload.items);
    case 'pttavm':
      return array(payload.lines ?? payload.siparisUrunler);
    case 'koctas':
    case 'teknosa':
      return array(payload.lines ?? payload.order_lines);
    case 'shopify':
      return array(payload.lines ?? (isObject(payload.lineItems) ? payload.lineItems.nodes : undefined));
    default:
      // Trendyol, n11 and an order already reduced
      return array(payload.lines);
  }
}

/**
 * A marketplace's copy of an order with the buyer taken out: `{ lines: [...] }` with only the
 * PAYLOAD_LINE_KEYS of each line (the shape the warehouse's own label reads lines from). Null for
 * an order without a payload (manual orders). Applying it again changes nothing.
 */
export function erasedChannelPayload(channel: MarketplaceChannel | string, payload: unknown): { lines: Json[] } | null {
  if (payload === null || payload === undefined) return null;
  if (!isObject(payload)) return { lines: [] };
  const lines = linesOf(channel, payload)
    .map((line) => (isObject(line) ? keep(line) : undefined))
    .filter((line): line is Json => line !== undefined);
  return { lines };
}
